import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Injectable, signal } from '@angular/core';
import {
    markNotificationAsRead,
    NotificationEvent,
    openNotificationDetail,
    pruneAllReadNotifications,
} from '@interacta-shared/data-access-notification';
import { Store } from '@ngrx/store';
import { filter, merge, takeUntil } from 'rxjs';
import { NotificationPanelOverlayComponent } from '../components/notification-panel-overlay/notification-panel-overlay.component';

@Injectable({
    providedIn: 'root',
})
export class NotificationPanelService {
    private readonly overlay = inject(Overlay);
    private readonly store = inject(Store);

    private overlayRef: OverlayRef;

    private readonly _isOpen = signal(false);
    readonly isOpen = this._isOpen.asReadonly();

    constructor() {
        const config = new OverlayConfig({
            positionStrategy: this.overlay
                .position()
                .global()
                .right('1rem')
                .centerVertically(),
            hasBackdrop: true,
            backdropClass: 'bg-dialog-layer',
        });

        this.overlayRef = this.overlay.create(config);
    }

    open() {
        if (!this.overlayRef.hasAttached()) {
            const componentPortal = new ComponentPortal(
                NotificationPanelOverlayComponent,
            );
            this.overlayRef.attach(componentPortal);
            merge(
                this.overlayRef.backdropClick(),
                this.overlayRef
                    .keydownEvents()
                    .pipe(filter(({ key }) => key === 'Escape')),
            )
                .pipe(takeUntil(this.overlayRef.detachments()))
                .subscribe(() => this.close());
        }
        this._isOpen.set(true);
    }

    close() {
        if (this.overlayRef.hasAttached()) {
            this.overlayRef.detach();
        }
        this._isOpen.set(false);
        this.store.dispatch(pruneAllReadNotifications());
    }

    openNotificationDetail(notification: NotificationEvent) {
        this.close();
        this.store.dispatch(
            openNotificationDetail({ notification, navigate: false }),
        );
    }

    markNotificationAsRead(notification: NotificationEvent) {
        this.store.dispatch(markNotificationAsRead({ notification }));
    }
}
